/**
 * Builds the read-only summary shown in the compliance-task create
 * confirmation dialog (NAU-336). Kept free of React/component imports so the
 * pure transformation can be unit-tested under the node-env vitest config,
 * mirroring external-service-fields.ts.
 */
import { format } from "date-fns";

import {
  FREQUENCY_OPTIONS,
  ROLE_DISPLAY,
  sortCategoryIdsByCatalog,
} from "./compliance-constants";

export interface SummaryRow {
  label: string;
  value: string;
}

export interface SummaryGroup {
  title: string;
  rows: SummaryRow[];
}

/** The subset of the create form's data the summary renders. */
export interface ComplianceTaskSummaryData {
  name: string;
  description?: string;
  primaryCategoryId: string;
  secondaryCategoryIds: string[];
  startDate: Date;
  frequency: string;
  assignmentType: "role" | "person";
  assigneeRole?: string;
  assigneeUserId?: string;
  evidenceRequired: boolean;
  externalServiceFields?: { label: string }[];
}

interface SummaryCategory {
  id: string;
  name: string;
}

interface SummaryMember {
  id?: string;
  userId?: string;
  firstName?: string;
  lastName?: string;
  user?: { firstName?: string; lastName?: string } | null;
}

interface SummaryContext {
  categories: SummaryCategory[];
  members: SummaryMember[];
  nurseryName?: string;
}

export function buildComplianceTaskSummary(
  data: ComplianceTaskSummaryData,
  { categories, members, nurseryName }: SummaryContext,
): SummaryGroup[] {
  return [
    { title: "Task details", rows: taskDetailRows(data, categories) },
    { title: "Schedule", rows: scheduleRows(data) },
    {
      title: "Responsibility",
      rows: responsibilityRows(data, members, nurseryName),
    },
    { title: "Settings", rows: settingsRows(data) },
  ];
}

function categoryName(
  id: string,
  categories: SummaryCategory[],
): string | undefined {
  return categories.find((c) => c.id === id)?.name;
}

function taskDetailRows(
  data: ComplianceTaskSummaryData,
  categories: SummaryCategory[],
): SummaryRow[] {
  const rows: SummaryRow[] = [{ label: "Task name", value: data.name }];

  const description = data.description?.trim();
  if (description) {
    rows.push({ label: "Description", value: description });
  }

  rows.push({
    label: "Primary category",
    value: categoryName(data.primaryCategoryId, categories) ?? "—",
  });

  // Order secondaries by the catalog so they read in the same order as the
  // list-view badges (sortCategoryIdsByCatalog).
  const secondaryNames = sortCategoryIdsByCatalog(
    data.secondaryCategoryIds,
    categories,
  )
    .map((id) => categoryName(id, categories))
    .filter((name): name is string => Boolean(name));
  rows.push({
    label: "Secondary categories",
    value: secondaryNames.length > 0 ? secondaryNames.join(", ") : "None",
  });

  return rows;
}

function scheduleRows(data: ComplianceTaskSummaryData): SummaryRow[] {
  const frequencyLabel =
    FREQUENCY_OPTIONS.find((o) => o.value === data.frequency)?.label ??
    data.frequency;
  const rows: SummaryRow[] = [];

  // startDate is optional in edit mode (legacy tasks may lack it, or it may be
  // locked and not in the form payload).
  if (data.startDate) {
    rows.push({
      label: "First due date",
      value: format(data.startDate, "EEEE, MMMM d, yyyy"),
    });
  }

  rows.push({ label: "Repeat frequency", value: frequencyLabel });
  return rows;
}

function responsibilityRows(
  data: ComplianceTaskSummaryData,
  members: SummaryMember[],
  nurseryName: string | undefined,
): SummaryRow[] {
  return [
    { label: "Assigned to", value: assigneeLabel(data, members) },
    { label: "Nursery", value: nurseryName ?? "—" },
  ];
}

function assigneeLabel(
  data: ComplianceTaskSummaryData,
  members: SummaryMember[],
): string {
  if (data.assignmentType === "role") {
    const role = data.assigneeRole;
    if (!role) {
      return "—";
    }
    return ROLE_DISPLAY[role] ?? role;
  }

  const member = members.find(
    (m) => (m.userId ?? m.id) === data.assigneeUserId,
  );
  if (!member) {
    return "—";
  }
  const firstName = member.user?.firstName ?? member.firstName ?? "";
  const lastName = member.user?.lastName ?? member.lastName ?? "";
  return `${firstName} ${lastName}`.trim() || "—";
}

function settingsRows(data: ComplianceTaskSummaryData): SummaryRow[] {
  const rows: SummaryRow[] = [
    {
      label: "Evidence upload required",
      value: data.evidenceRequired ? "Yes" : "No",
    },
  ];

  const fields = data.externalServiceFields ?? [];
  if (fields.length > 0) {
    const labels = fields
      .map((f) => f.label.trim() || "Untitled field")
      .join(", ");
    rows.push({
      label: "Service detail fields",
      value: `${fields.length} field${fields.length === 1 ? "" : "s"}: ${labels}`,
    });
  }

  return rows;
}
